
import React, { useState, useContext } from "react";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
} from "@mui/material";

import {
  Email,
  ArrowBack,
  VerifiedUser,
} from "@mui/icons-material";

import { motion, AnimatePresence } from "framer-motion";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import api from "../api/api";
import { AuthContext } from "../AuthContext";

const LoginAuthentication = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { setUser } = useContext(AuthContext);

  const email =
    location.state?.email ||
    localStorage.getItem("auth_email");

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // --------------------------------------------------
  // REDIRECT USER
  // --------------------------------------------------

  const redirectUser = (user) => {
    const role = (
      user.user_type ||
      user.role ||
      ""
    ).toLowerCase().trim();

    const routes = {
      owner: "/owner-dashboard",
      admin: "/newAdminDashboard",
      proprietor: "/newPropariatorDashboard",
      principal: "/newPrincipalDashboard",
      teacher: "/teacher-dashboard",
      student: "/student-dashboard",
      parent: "/parentsDashboard",
      registry: "/registerDashboard",
      vpi: "/vpiDashboard",
      vpa: "/vpaDashboard",
      "it personel": "/it-dashboard",
      "it personnel": "/it-dashboard",
      business_manager: "/businessManagerDashboard",
      sales_agent: "/sales-dashboard",
    };

    navigate(routes[role] || "/", {
      replace: true,
    });
  };

  // --------------------------------------------------
  // VERIFY CODE
  // --------------------------------------------------

  const verifyCode = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!email) {
      setMessage(
        "Authentication email missing. Please login again."
      );
      return;
    }

    if (!code.trim()) {
      setMessage("Please enter your authentication code.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await api.post(
        "/api/users/verify-authentication-code/",
        {
          email,
          code: code.trim(),
        }
      );

      const access = response.data.access;
      const refresh = response.data.refresh;

      if (!access || !refresh) {
        throw new Error(
          "Authentication failed. Tokens missing."
        );
      }

      localStorage.setItem("access", access);
      localStorage.setItem("refresh", refresh);

      window.dispatchEvent(
        new Event("user-logged-in")
      );

      const profile = await api.get(
        "/api/user-profile/"
      );

      const user = profile.data;

      setUser(user);

      localStorage.setItem(
        "user",
        JSON.stringify(user)
      );

      localStorage.removeItem("auth_email");

      redirectUser(user);

    } catch (error) {
      console.error(error);

      setMessage(
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        "Invalid authentication code."
      );

    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        px: 2,
        py: 4,
        overflow: "hidden",
        position: "relative",

        background:
          "linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 50%, #FFF8F1 100%)",
      }}
    >
      {/* SUBTLE BACKGROUND ACCENTS */}

      <Box
        component={motion.div}
        animate={{
          y: [0, -15, 0],
          x: [0, 8, 0],
        }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          width: 220,
          height: 220,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,143,54,0.10), transparent 70%)",
          top: -80,
          right: -60,
          pointerEvents: "none",
        }}
      />

      <Box
        component={motion.div}
        animate={{
          y: [0, 15, 0],
          x: [0, -8, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          width: 200,
          height: 200,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,115,0,0.07), transparent 70%)",
          bottom: -80,
          left: -60,
          pointerEvents: "none",
        }}
      />

      {/* FORM CARD */}

      <Box
        component={motion.div}
        initial={{
          opacity: 0,
          y: 30,
          scale: 0.98,
        }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
        }}
        transition={{
          duration: 0.6,
          ease: "easeOut",
        }}
        sx={{
          width: "100%",
          maxWidth: 440,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            boxSizing: "border-box",
            p: {
              xs: 3,
              sm: 4.5,
            },
            borderRadius: {
              xs: "20px",
              sm: "25px",
            },
            background: "#FFFFFF",
            border: "1px solid #F0E8E1",
            boxShadow:
              "0 18px 55px rgba(31, 41, 55, 0.08), 0 3px 12px rgba(230,107,0,0.05)",
          }}
        >
          {/* ICON */}

          <Box
            component={motion.div}
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              delay: 0.15,
              duration: 0.45,
            }}
            sx={{
              width: 65,
              height: 65,
              mx: "auto",
              mb: 2.5,
              borderRadius: "19px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, #FFF0DF, #FFE0C1)",
              border: "1px solid #FFE1C5",
              boxShadow:
                "0 6px 18px rgba(230,107,0,0.09)",
            }}
          >
            <VerifiedUser
              sx={{
                fontSize: 34,
                color: "#E87516",
              }}
            />
          </Box>

          {/* TITLE */}

          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              fontWeight: 900,
              fontSize: {
                xs: "25px",
                sm: "29px",
              },
              letterSpacing: "-0.8px",
              color: "#172B43",
              mb: 1,
            }}
          >
            Verify Login
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              color: "#7A8795",
              fontSize: "13.5px",
              lineHeight: 1.7,
              mb: 3.5,
            }}
          >
            Enter the authentication code sent to your email.
          </Typography>

          {/* EMAIL */}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              p: 1.5,
              mb: 3,
              borderRadius: "13px",
              background: "#FFF9F3",
              border: "1px solid #F8E8D9",
              minWidth: 0,
            }}
          >
            <Email
              sx={{
                color: "#E87516",
                fontSize: 21,
                flexShrink: 0,
              }}
            />

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 800,
                  color: "#A18D7B",
                  textTransform: "uppercase",
                  letterSpacing: "0.7px",
                  mb: 0.3,
                }}
              >
                Email address
              </Typography>

              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#35465A",
                  overflowWrap: "anywhere",
                }}
              >
                {email || "Email unavailable"}
              </Typography>
            </Box>
          </Box>

          {/* FORM */}

          <form onSubmit={verifyCode}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 800,
                color: "#34465A",
                mb: 1,
              }}
            >
              AUTHENTICATION CODE
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter authentication code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (message) setMessage("");
              }}
              required
              disabled={loading}
              autoComplete="one-time-code"
              inputProps={{
                "aria-label": "Authentication code",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VerifiedUser
                      sx={{
                        color: "#E87516",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2.5,

                "& .MuiOutlinedInput-root": {
                  minHeight: 55,
                  borderRadius: "13px",
                  background: "#FAFBFC",
                  transition: "all .25s ease",

                  "& fieldset": {
                    borderColor: "#E5E9EF",
                  },

                  "&:hover fieldset": {
                    borderColor: "#F0A15F",
                  },

                  "&.Mui-focused": {
                    background: "#FFFFFF",
                    boxShadow:
                      "0 0 0 4px rgba(232,117,22,0.08)",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: "#E87516",
                    borderWidth: "1.5px",
                  },
                },

                "& input": {
                  fontSize: "14px",
                  fontWeight: 600,
                },
              }}
            />

            {/* ERROR MESSAGE */}

            <AnimatePresence>
              {message && (
                <motion.div
                  initial={{
                    opacity: 0,
                    y: -8,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                    y: -8,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      mb: 2.5,
                      borderRadius: "11px",
                      background: "#FFF1F0",
                      border: "1px solid #FFD5D2",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#C0392B",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {message}
                    </Typography>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>

            {/* VERIFY BUTTON */}

            <Button
              type="submit"
              fullWidth
              disabled={loading || !code.trim() || !email}
              sx={{
                minHeight: 54,
                borderRadius: "13px",
                color: "#FFFFFF",
                fontSize: "14px",
                fontWeight: 900,
                textTransform: "none",
                background:
                  "linear-gradient(110deg, #FF922E 0%, #F07816 55%, #E36508 100%)",
                boxShadow:
                  "0 7px 18px rgba(230,107,0,0.19)",
                transition: "all .25s ease",

                "&:hover": {
                  background:
                    "linear-gradient(110deg, #F5821F, #D95C00)",
                  boxShadow:
                    "0 10px 24px rgba(230,107,0,0.28)",
                  transform: "translateY(-2px)",
                },

                "&:active": {
                  transform: "translateY(0)",
                },

                "&.Mui-disabled": {
                  color: "#FFFFFF",
                  opacity: 0.6,
                  background: "#C7CDD4",
                },
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress
                    size={19}
                    sx={{ color: "#FFFFFF" }}
                  />
                  Verifying...
                </Box>
              ) : (
                "Verify Code"
              )}
            </Button>

            {/* BACK BUTTON */}

            <Button
              fullWidth
              variant="outlined"
              disabled={loading}
              onClick={() => navigate("/login")}
              startIcon={<ArrowBack />}
              sx={{
                mt: 1.8,
                minHeight: 51,
                borderRadius: "13px",
                fontWeight: 800,
                fontSize: "13px",
                textTransform: "none",
                color: "#596B7D",
                border: "1.5px solid #E1E6EC",
                transition: "all .25s ease",

                "&:hover": {
                  color: "#E87516",
                  borderColor: "#F0A15F",
                  background: "#FFF9F3",
                },
              }}
            >
              Back To Login
            </Button>
          </form>

          {/* FOOTER */}

          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: "1px solid #F0F1F3",
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                fontSize: "11px",
                color: "#9AA3AD",
                fontWeight: 600,
              }}
            >
              <VerifiedUser
                sx={{
                  fontSize: 13,
                  verticalAlign: "middle",
                  mr: 0.5,
                  color: "#E87516",
                }}
              />
              Secure Authentication
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginAuthentication;
